import { motion } from "framer-motion";
import { Users, Crown, Sparkles } from "lucide-react";
import teamCoaching from "@/assets/team-coaching.jpg";
import executiveCoaching from "@/assets/executive-coaching.jpg";
import burkhardStephanCoaching from "@/assets/burkhard-stephan-coaching.jpg";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const offerings = [
  {
    icon: Sparkles,
    title: "Begleitung von Young Professionals",
    description:
      "Ob Fragen zur angestrebten Ausbildung, Übergang in die Arbeitswelt oder Wechsel in die erste Führungsposition: Wir unterstützen Dich dabei, Klarheit zu gewinnen, Deine neue Rolle zu definieren und souverän auszufüllen.",
    image: burkhardStephanCoaching,
    imageAlt: "Burkhard Rathgeber und Stephan Rathgeber – Begleitung von Young Professionals",
  },
  {
    icon: Users,
    title: "Einzel- & Teamcoaching",
    description:
      "Wir lösen Knoten. Ob es um persönliche Blockaden geht oder um Sand im Getriebe eines Teams. Wir nutzen systemische und neurowissenschaftliche Interventionen, um Zusammenarbeit effektiv und freudvoll zu machen.",
    image: teamCoaching,
    imageAlt: "Das Leadership Focus Team – Einzel- & Teamcoaching",
  },
  {
    icon: Crown,
    title: "Executive Coaching",
    description:
      "Die Luft an der Spitze ist dünn. Wir bieten Ihnen strategisches Coaching auf Augenhöhe. Diskret, direkt und fokussiert auf Ihre spezifischen Herausforderungen als Top-Entscheider:in.",
    image: executiveCoaching,
    imageAlt: "Margret Klinkhammer und Stephan Rathgeber – Executive Coaching",
  },
];

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Business Coaching",
  description: "Individuelles Coaching für Führungskräfte und Teams – Einzel- & Teamcoaching, Executive Coaching und Begleitung von Young Professionals. Neurowissenschaftlich fundiert und systemisch.",
  provider: {
    "@type": "Organization",
    name: "Leadership Focus GmbH",
    url: "https://leadership-focus.de",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  serviceType: "Business Coaching",
};

const Coaching = () => (
  <>
    <SEOHead title="Business Coaching – Leadership Focus" description="Individuelles Coaching für Führungskräfte und Teams. Persönliche und professionelle Weiterentwicklung mit Leadership Focus München." canonical="https://leadership-focus.de/portfolio/coaching" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />
    <Header />
    <main className="py-20 md:py-28">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center mb-14"
        >
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Business Coaching
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unsere individuelle Begleitung. Ihr Beschleuniger für Klarheit & Performance.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {offerings.map((item) => (
            <motion.div key={item.title} variants={fadeInUp}>
              <Card className="h-full border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group overflow-hidden">
                <div className="overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="w-full h-56 object-cover object-[center_20%] group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2 font-serif">{item.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </main>
    <Footer />
  </>
);

export default Coaching;
